// @ts-nocheck
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Eye, Edit, Trash2, Search } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Projeto {
  id: string;
  nome: string;
  prazo: string;
  valor_maximo: number;
  created_at: string;
  cliente?: { nome: string };
  empresa?: { razao_social: string };
  fornecedor?: { razao_social: string };
  local?: { nome: string };
}

interface FilterOptions {
  clientes: { id: string; nome: string }[];
  empresas: { id: string; razao_social: string }[];
  fornecedores: { id: string; razao_social: string }[];
  locais: { id: string; nome: string }[];
}

export default function PropostasProjetos() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [projetos, setProjetos] = useState<Projeto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    clientes: [],
    empresas: [],
    fornecedores: [],
    locais: []
  });
  const [filters, setFilters] = useState({
    cliente: "",
    empresa: "",
    fornecedor: "",
    local: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadProjetos();
  }, [searchTerm, filters]);

  const loadData = async () => {
    try {
      const [clientesRes, empresasRes, fornecedoresRes, locaisRes] = await Promise.all([
        supabase.from("clientes").select("id, nome").eq("ativo", true),
        supabase.from("empresas").select("id, razao_social").eq("ativo", true),
        supabase.from("fornecedores").select("id, razao_social").eq("ativo", true),
        supabase.from("locais").select("id, nome").eq("ativo", true)
      ]);

      setFilterOptions({
        clientes: clientesRes.data || [],
        empresas: empresasRes.data || [],
        fornecedores: fornecedoresRes.data || [],
        locais: locaisRes.data || []
      });

      await loadProjetos();
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados",
        variant: "destructive"
      });
    }
  };

  const loadProjetos = async () => {
    try {
      let query = supabase
        .from("projetos")
        .select(`
          *,
          cliente:clientes(nome),
          empresa:empresas(razao_social),
          fornecedor:fornecedores(razao_social),
          local:locais(nome)
        `)
        .order("created_at", { ascending: false });

      if (searchTerm) {
        query = query.ilike("nome", `%${searchTerm}%`);
      }

      if (filters.cliente) {
        query = query.eq("id_cliente", filters.cliente);
      }

      if (filters.empresa) {
        query = query.eq("id_empresa", filters.empresa);
      }

      if (filters.fornecedor) {
        query = query.eq("id_fornecedor", filters.fornecedor);
      }

      if (filters.local) {
        query = query.eq("id_local", filters.local);
      }

      const { data, error } = await query;

      if (error) throw error;

      setProjetos(data || []);
    } catch (error) {
      console.error("Erro ao carregar projetos:", error);
      toast({
        title: "Erro",
        description: "Erro ao carregar projetos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("projetos")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Projeto excluído com sucesso"
      });

      loadProjetos();
    } catch (error) {
      console.error("Erro ao excluir projeto:", error);
      toast({
        title: "Erro",
        description: "Erro ao excluir projeto",
        variant: "destructive"
      });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL"
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd/MM/yyyy", { locale: ptBR });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilters({
      cliente: "",
      empresa: "",
      fornecedor: "",
      local: ""
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Projetos</h1>
          <p className="text-muted-foreground">
            Gerencie os projetos das propostas
          </p>
        </div>
        <Button onClick={() => navigate("/intranet/propostas/projetos/new")}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Projeto
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={filters.cliente} onValueChange={(value) => setFilters({ ...filters, cliente: value || "" })}>
              <SelectTrigger>
                <SelectValue placeholder="Cliente" />
              </SelectTrigger>
              <SelectContent>
                {filterOptions.clientes.map((cliente) => (
                  <SelectItem key={cliente.id} value={cliente.id}>
                    {cliente.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.empresa} onValueChange={(value) => setFilters({ ...filters, empresa: value || "" })}>
              <SelectTrigger>
                <SelectValue placeholder="Empresa" />
              </SelectTrigger>
              <SelectContent>
                {filterOptions.empresas.map((empresa) => (
                  <SelectItem key={empresa.id} value={empresa.id}>
                    {empresa.razao_social}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.fornecedor} onValueChange={(value) => setFilters({ ...filters, fornecedor: value || "" })}>
              <SelectTrigger>
                <SelectValue placeholder="Fornecedor" />
              </SelectTrigger>
              <SelectContent>
                {filterOptions.fornecedores.map((fornecedor) => (
                  <SelectItem key={fornecedor.id} value={fornecedor.id}>
                    {fornecedor.razao_social}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.local} onValueChange={(value) => setFilters({ ...filters, local: value || "" })}>
              <SelectTrigger>
                <SelectValue placeholder="Local" />
              </SelectTrigger>
              <SelectContent>
                {filterOptions.locais.map((local) => (
                  <SelectItem key={local.id} value={local.id}>
                    {local.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={clearFilters}>
              Limpar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Projetos Cadastrados</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center p-8">
              <div className="text-muted-foreground">Carregando...</div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Prazo</TableHead>
                  <TableHead>Valor Máximo</TableHead>
                  <TableHead>Data de Criação</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projetos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Nenhum projeto encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  projetos.map((projeto) => (
                    <TableRow key={projeto.id}>
                      <TableCell className="font-medium">{projeto.nome}</TableCell>
                      <TableCell>
                        {projeto.cliente?.nome ? (
                          <Badge variant="secondary">{projeto.cliente.nome}</Badge>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {projeto.empresa?.razao_social ? (
                          <Badge variant="secondary">{projeto.empresa.razao_social}</Badge>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {projeto.prazo ? formatDate(projeto.prazo) : "-"}
                      </TableCell>
                      <TableCell>
                        {projeto.valor_maximo ? formatCurrency(projeto.valor_maximo) : "-"}
                      </TableCell>
                      <TableCell>{formatDate(projeto.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => navigate(`/intranet/propostas/projetos/view/${projeto.id}`)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => navigate(`/intranet/propostas/projetos/edit/${projeto.id}`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="icon">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja excluir o projeto "{projeto.nome}"?
                                  Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(projeto.id)}>
                                  Excluir
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}